import { Track, type TrackNode } from '@/world/Track';
import type { ShortcutSpec, ShortcutStyle, ShortcutLook, RampSpec } from '@/config/cities';
import { tuning } from '@/config/tuning';
import { clamp, shortestAngleDelta } from '@/math/angles';
import { forwardX, forwardZ, rightX, rightZ } from '@/math/orientation';

/**
 * shortcuts.ts — PURE physics for the SECRET SHORTCUTS (M117c aerial rework).
 * No three.js, no DOM, no RNG.
 *
 * THE MECHANIC (the owner's spec, second round: "no more tunnels or entrances
 * from the ground-level side — make all the shortcuts from kicker ramps"):
 * every shortcut is now a RAMP-LAUNCHED AERIAL PATH. You hit a launch ramp
 * with real speed, FLY, and either catch an elevated deck or thud back down —
 * Koopa Troopa Beach, faithfully. Three flavours:
 *
 *  - 'cavern' (Rio): a side kicker at the verge fires you AT a cavern mouth
 *    up a cliff face; clear the sill or bounce off the rock.
 *  - 'highline' (NYC): a bigger side kicker lofts you over the guardrail onto
 *    an ELEVATED PARK LINE that rides beside the course through the towers.
 *  - 'skypath' (Lagos): the city's own (enlarged) centred ramp is the
 *    launcher; carry enough speed and you catch an overhead deck hanging
 *    past the lip, which swings out over the lagoon.
 *
 * YOU DRIVE EVERYTHING. Inside, an entity is simply updated against the
 * channel's Track (deck floor + rail clamps) instead of the course's. The
 * ride ends down a descent chute and out a wide EXIT opening at road grade
 * (exits are not entrances — the skill gate is the flight in). Entities in
 * different spaces never interact; same-channel entities duel normally.
 *
 * HUMANS ONLY: SimCore gates every catch on !isBot, so decks are simply
 * uncatchable for bots (and bot balls) — the locked AI pace ladder is
 * untouched by construction, and a matchmade AI seat can never reveal itself
 * mid-air. Bots that clip a kicker just hop, land, and race on.
 *
 * DETERMINISM CONTRACT (online-critical): placement is a pure function of
 * the generated track (+ the city ramp spec); catch/exit transitions are
 * pure functions of synced entity state; the per-racer channel indices ride
 * the snapshot. Server and every predicting client agree from any snapshot.
 */

export type ShortcutKind = 'jump';

export const STYLE_KIND: Record<ShortcutStyle, ShortcutKind> = {
  cavern: 'jump',
  highline: 'jump',
  skypath: 'jump',
};

/** A wall-line aperture (the cavern mouth / the highline's catch edge over
 *  the guardrail): its width runs ALONG the course; a flying body passes when
 *  its flight line pierces the wall inside `arc ± (half − radius)` AT OR
 *  ABOVE the sill. Judged at the WALL LINE with the crossing extrapolated
 *  (bodies trigger at their clamp line a body-width short of it — measured,
 *  M117: the un-extrapolated check ate the whole fit margin). */
export interface DoorFrame {
  x: number;
  y: number;
  z: number;
  /** Course arc-length of the aperture centre. */
  arc: number;
  /** Which wall it pierces (sign of lateral). */
  side: -1 | 1;
  /** Aperture half-width along the wall. */
  half: number;
  /** |lateral| of the wall line itself (the track half-width at placement). */
  wallLat: number;
  /** Course forward at the door (unit, §6.1). */
  dirX: number;
  dirZ: number;
  /** Course right at the door (unit). */
  rightX: number;
  rightZ: number;
}

/** The skypath's OVERHEAD CATCH: a plane hanging in the air past the launch
 *  ramp's lip, on the ramp's flight line. Cross it flying at/above the sill,
 *  inside the width, and you land on the deck; road traffic passes UNDER it
 *  untouched. Local-frame geometry (the road bends under the straight
 *  flight — a road-relative catch would sit off the flight line, measured). */
export interface CatchFrame {
  x: number;
  z: number;
  /** Flight heading through the catch plane (§6.1 forward = (sin h, cos h)). */
  dir: number;
  /** Deck floor absolute y at the catch. */
  y: number;
  /** Minimum absolute y at the crossing (y − sillDrop forgiveness). */
  sillY: number;
  /** Catch half-width across the flight line. */
  half: number;
}

/** The jump kicker: a launch ramp at the verge, AIMED at the mouth (its own
 *  heading — not the course's). Physical for every body (bots hop + thud). */
export interface KickerFrame {
  x: number;
  y: number;
  z: number;
  /** Launch heading (§6.1). */
  dir: number;
  length: number;
  halfWidth: number;
  height: number;
}

/** A shortcut resolved onto a generated track (frozen once per race). */
export interface PlacedShortcut {
  style: ShortcutStyle;
  /** VISUAL dressing only (M117 batch 2): which city builder skins the deck
   *  (shortcutMeshes.ts dispatch). Resolved here — spec value or the style
   *  default — so the mesh layer never re-derives it. NOTHING in this file
   *  or the sim branches on it: physics per `style` is byte-identical. */
  look?: ShortcutLook;
  kind: ShortcutKind;
  /** Wall-entry styles (cavern/highline): the aperture over the guardrail.
   *  Also doubles as the placement anchor frame for all styles. */
  door: DoorFrame;
  /** Exit opening at road grade (wide — leaving is never the test). */
  exitDoor: DoorFrame;
  /** 'skypath' only: the overhead catch plane (entry is NOT via `door`). */
  catch?: CatchFrame;
  /** The drivable channel: a real mini-Track (uniform `chStep` nodes). Its
   *  halfWidth IS the deck rail clamp; its endWalls stop backing out. */
  channel: Track;
  /** Course arcs the standings lerp maps channel progress onto. */
  entryArc: number;
  exitArc: number;
  /** Channel distance where control transfers back to the road (the exit
   *  stub reaches inside the road — position-continuous hand-off). */
  leaveDist: number;
  /** Channel distance of the entry point along the spine (0 for jumps). */
  doorDist: number;
  /** Main-road arc of the hand-off point — the standings lerp's far end. */
  exitHandArc: number;
  /** Deck floor height above the entry's road grade + the wall-entry sill. */
  mouthY?: number;
  sillY?: number;
  /** Side-kicker (cavern/highline; the skypath launches off the CITY ramp). */
  kicker?: KickerFrame;
  /** Spine points at/above grade (all of them, for aerial paths) — the
   *  scenery-clearing right-of-way. */
  surfacePts: { x: number; z: number }[];
}

/** Over how many units before the exit the deck's ground-clearance floor
 *  relaxes to zero (M117d — see the height-profile pass). */
const CLEAR_RUNIN = 70;

/** Per-node |curvature| in rad/u for the whole track (index i = node i→i+1). */
function nodeCurvatures(track: Track): number[] {
  const out: number[] = [];
  for (let i = 0; i < track.nodes.length - 1; i++) {
    out.push(
      Math.abs(shortestAngleDelta(track.nodes[i].dir, track.nodes[i + 1].dir)) / track.step,
    );
  }
  return out;
}

/**
 * PLACEMENT REFINEMENT (the owner's placement rule, encoded): a launch must
 * sit at the end of a straight, measured RUNWAY — speed has to be buildable,
 * and never on the tight side of a bend. The spec's `at` is a preference;
 * the placer slides it ± `refineSpan` of the course to the candidate whose
 * runway window (ending `approachOffset` before it — at the kicker) has the
 * LEAST worst curvature. Pure + deterministic (dev ?seed= overrides land
 * their launches on that track's straights too).
 */
export function refineEntryArc(
  track: Track,
  prefArc: number,
  approachLen: number,
  approachOffset = 0,
): number {
  const cfg = tuning.shortcuts;
  const curv = nodeCurvatures(track);
  const span = cfg.refineSpan * track.finishDistance;
  const lo = Math.max(approachOffset + approachLen + 20, prefArc - span);
  const hi = Math.min(track.finishDistance - 60, prefArc + span);
  const appNodes = Math.max(1, Math.round(approachLen / track.step));
  let bestArc = prefArc;
  let bestScore = Infinity;
  for (let arc = lo; arc <= hi; arc += track.step) {
    const n = Math.min(
      curv.length - 1,
      Math.max(0, Math.round((arc - approachOffset) / track.step)),
    );
    let worst = 0;
    for (let i = Math.max(0, n - appNodes); i < n; i++) worst = Math.max(worst, curv[i]);
    // The preference anchor must outweigh MICRO-curvature noise (a straight's
    // 0.02-vs-0.006 deg/u ripple) or the door wanders tens of units from the
    // hand-picked spot toward whatever is micro-cleanest — measured: it slid
    // 32 u back toward Bow Bridge. Only a REAL bend should move it.
    const score = worst + Math.abs(arc - prefArc) * 2e-5;
    if (score < bestScore) {
      bestScore = score;
      bestArc = arc;
    }
  }
  return bestArc;
}

function doorFrameAt(track: Track, arc: number, side: -1 | 1, half: number): DoorFrame {
  const s = track.sampleAt(arc);
  const x = s.x + s.rightX * side * track.halfWidth;
  const z = s.z + s.rightZ * side * track.halfWidth;
  return {
    x,
    y: track.heightAt(x, z),
    z,
    arc,
    side,
    half,
    wallLat: track.halfWidth,
    dirX: forwardX(s.dir),
    dirZ: forwardZ(s.dir),
    rightX: s.rightX,
    rightZ: s.rightZ,
  };
}

/** Resolve a city's shortcut specs onto a generated track (once per race).
 *  `ramp` is the city's own launch ramp — the 'skypath' style anchors its
 *  overhead catch to that ramp's REAL lip and flight line. */
export function placeShortcuts(
  track: Track,
  specs: readonly ShortcutSpec[],
  ramp?: RampSpec,
): PlacedShortcut[] {
  const cfg = tuning.shortcuts;
  return specs.map((spec) => {
    const side = spec.side;
    const isSky = spec.style === 'skypath';
    const kdims = spec.style === 'highline' ? cfg.kickerBig : cfg.kicker;

    // ---- The entry anchor -------------------------------------------------
    // Wall styles: refine the door so the KICKER gets its straight runway.
    // Skypath: the entry is the city ramp itself — the owner placed it; the
    // catch hangs off its real lip (no refinement, no runway contract).
    let entryArc: number;
    let lipY = 0;
    let flightDir = 0;
    let lipX = 0;
    let lipZ = 0;
    if (isSky && ramp) {
      const rampStart = ramp.at * track.finishDistance;
      const lipArc = rampStart + ramp.length;
      const lip = track.sampleAt(lipArc);
      // Launch headings pivot around the MIDDLE of the ramp (players steer
      // along the curving road until they commit) — anchoring the flight
      // line to the lip tangent put the porch ~25° off real flights
      // (measured); the mid-ramp tangent centres the spread.
      flightDir = track.sampleAt(rampStart + ramp.length * 0.5).dir;
      lipX = lip.x;
      lipZ = lip.z;
      lipY = lip.y + ramp.height;
      entryArc = lipArc + cfg.catchAhead;
    } else {
      entryArc = refineEntryArc(
        track,
        spec.at * track.finishDistance,
        cfg.approachLen,
        kdims.back,
      );
    }
    const exitArc = spec.exitAt * track.finishDistance;

    const lift =
      spec.mouthLift ??
      (spec.style === 'cavern' ? cfg.mouthLift : spec.style === 'highline' ? cfg.highlineLift : cfg.skypathLift);
    const door = doorFrameAt(track, entryArc, side, cfg.mouthHalf);
    const exitDoor = doorFrameAt(track, exitArc, side, cfg.exitHalf);

    // ---- The side kicker (wall styles) ------------------------------------
    // Computed BEFORE the spine: the spine's first stretch must run along the
    // KICKER'S AIM LINE — the landing arrives on that line, and a spine that
    // veered off it let the deck's rail clamp snap the just-landed car
    // sideways by several units in one step (measured).
    let kicker: KickerFrame | undefined;
    if (!isSky) {
      const ks = track.sampleAt(Math.max(20, entryArc - kdims.back));
      const kx = ks.x + ks.rightX * side * (track.halfWidth - kdims.inset);
      const kz = ks.z + ks.rightZ * side * (track.halfWidth - kdims.inset);
      kicker = {
        x: kx,
        y: track.heightAt(kx, kz),
        z: kz,
        dir: Math.atan2(door.x - kx, door.z - kz),
        length: kdims.length,
        halfWidth: kdims.halfWidth,
        height: kdims.height,
      };
    }

    // ---- Plan-view spine --------------------------------------------------
    // Start point: wall styles begin AT the wall aperture heading along the
    // kicker's flight line; the skypath begins at the landing porch ON THE
    // RAMP'S FLIGHT LINE (the road bends under a straight flight — a
    // road-relative start would sit off it, measured).
    let startX: number;
    let startZ: number;
    let inDirX: number;
    let inDirZ: number;
    const ca = Math.cos(cfg.doorAngle);
    const sa = Math.sin(cfg.doorAngle);
    if (isSky) {
      startX = lipX + forwardX(flightDir) * cfg.catchAhead;
      startZ = lipZ + forwardZ(flightDir) * cfg.catchAhead;
      inDirX = forwardX(flightDir);
      inDirZ = forwardZ(flightDir);
    } else {
      // The highline's deck leading edge stands OFF beyond the wall crossing
      // (owner: the entrance tucks at the course edge, away from the ramp) —
      // you fly the extra stretch in channel space and land descending onto
      // the floor. The cavern keeps its edge at the wall face.
      const standoff = spec.style === 'highline' ? cfg.deckStandoff : 0;
      const kdir = (kicker as KickerFrame).dir;
      startX = door.x + forwardX(kdir) * standoff;
      startZ = door.z + forwardZ(kdir) * standoff;
      inDirX = forwardX(kdir);
      inDirZ = forwardZ(kdir);
    }
    const outDirX = exitDoor.dirX * ca - exitDoor.rightX * side * sa;
    const outDirZ = exitDoor.dirZ * ca - exitDoor.rightZ * side * sa;

    // Outward bow: bulge the mid-path toward the shortcut's side — through
    // the towers, out over the lagoon, into the hillside.
    const bow = spec.bow ?? 0;
    let bowX = (door.rightX + exitDoor.rightX) * side;
    let bowZ = (door.rightZ + exitDoor.rightZ) * side;
    const bowLen = Math.hypot(bowX, bowZ) || 1;
    bowX /= bowLen;
    bowZ /= bowLen;

    // Dense polyline: a straight HOLD segment along the flight line, then a
    // hermite to the exit door, then the exit stub reaching INSIDE the road.
    // The hold is load-bearing (measured): the hermite's exit-ward pull
    // cancelled the aim direction near the start, curling the deck back over
    // the road — and straight through Bow Bridge's masonry. Landing + the
    // first seconds of the ride stay ON the line the launcher aimed.
    const HOLD: Record<ShortcutStyle, number> = { cavern: 18, highline: 36, skypath: 30 };

    const hold = HOLD[spec.style];
    const dense: { x: number; z: number }[] = [];
    const stub = cfg.stubLen;
    for (let i = 0; i < 12; i++) {
      const t = i / 12;
      dense.push({ x: startX + inDirX * hold * t, z: startZ + inDirZ * hold * t });
    }
    const hx = startX + inDirX * hold;
    const hz = startZ + inDirZ * hold;
    const chord = Math.hypot(exitDoor.x - hx, exitDoor.z - hz);
    const tanLen = chord * 0.4;
    const N = 96;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const t2 = t * t;
      const t3 = t2 * t;
      const h00 = 2 * t3 - 3 * t2 + 1;
      const h10 = t3 - 2 * t2 + t;
      const h01 = -2 * t3 + 3 * t2;
      const h11 = t3 - t2;
      const arch = bow * 4 * t * (1 - t);
      dense.push({
        x: h00 * hx + h10 * inDirX * tanLen + h01 * exitDoor.x + h11 * outDirX * tanLen + bowX * arch,
        z: h00 * hz + h10 * inDirZ * tanLen + h01 * exitDoor.z + h11 * outDirZ * tanLen + bowZ * arch,
      });
    }
    for (let i = 1; i <= 8; i++) {
      const t = i / 8;
      dense.push({ x: exitDoor.x + outDirX * stub * t, z: exitDoor.z + outDirZ * stub * t });
    }

    // Uniform resample at chStep (Track math assumes uniform node spacing).
    const cum: number[] = [0];
    for (let i = 1; i < dense.length; i++) {
      cum.push(cum[i - 1] + Math.hypot(dense[i].x - dense[i - 1].x, dense[i].z - dense[i - 1].z));
    }
    const total = cum[cum.length - 1];
    const nNodes = Math.max(4, Math.round(total / cfg.chStep) + 1);
    const step = total / (nNodes - 1);
    const pts: { x: number; z: number }[] = [];
    let seg = 1;
    for (let i = 0; i < nNodes; i++) {
      const d = Math.min(total, i * step);
      while (seg < cum.length - 1 && cum[seg] < d) seg++;
      const a = dense[seg - 1];
      const b = dense[seg];
      const span = cum[seg] - cum[seg - 1] || 1;
      const t = (d - cum[seg - 1]) / span;
      pts.push({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });
    }

    // ---- Height profile ---------------------------------------------------
    // The DECK rides at absolute heights (a level line over uneven ground /
    // water reads right); the chute + stub blend back to LOCAL grade so the
    // road hand-off is seamless whatever the terrain does at the exit.
    // Per style: skypath/cavern land ON the floor (sill just below it); the
    // highline's `lift` is the SILL at the wall crossing — the longer flight
    // drops ~1 u over the standoff, so the floor sits 1.2 below the sill and
    // you land DESCENDING onto it (no pop-up snap at threshold speed).
    let deckStartY: number;
    let sillY: number;
    if (isSky) {
      deckStartY = lipY + (lift - (ramp?.height ?? 0));
      sillY = deckStartY - cfg.sillDrop;
    } else if (spec.style === 'highline') {
      sillY = door.y + lift;
      deckStartY = sillY - 1.2;
    } else {
      deckStartY = door.y + lift;
      sillY = deckStartY - cfg.sillDrop;
    }
    // M117d — DOWNHILL ALL THE WAY (the owner's rule: "have the shortcuts be
    // mostly downhill so that if a ball makes it onto the shortcut without
    // the user it continues down the path to the exit"). The old profile held
    // a level deck then dumped down a 24 u chute, so a loose ball simply
    // parked up there. Now the ride FALLS continuously from the catch to the
    // exit, in three passes so the fall is guaranteed AND the deck never
    // sinks into the terrain it crosses:
    //   1. an eased straight descent, catch height → exit road grade;
    //   2. a floor at (ground + clearance), tapering to 0 at the exit so the
    //      hand-off lands ON the road;
    //   3. a BACKWARD max sweep over the ride, which turns any terrain-forced
    //      rise into a level plateau instead of an UPHILL (a rolling ball
    //      must never have to climb), capped at the catch height so a lander
    //      always meets the floor the flight was aimed at.
    const exitDist = total - stub;
    const nExit = Math.min(pts.length - 1, Math.round(exitDist / step));
    const grades = pts.map((p) => track.heightAt(p.x, p.z));
    // The pure CONCAVE fall line, catch height → exit grade: barely any drop
    // early, accelerating into the exit. The shape matters — an even (or
    // front-loaded) descent sank the Lagos sky-path to 2 u over the lagoon
    // bank within a third of its run (measured), which is not a sky-path.
    // This keeps the ride UP among the towers / out over the water for most
    // of its length and still never stops falling, so a loose ball keeps
    // rolling the whole way. (Stashed per node — the crest-relief pass below
    // uses it as its lower bound.)
    const descents = pts.map((_, i) => {
      const t = clamp((i * step) / Math.max(1, exitDist), 0, 1);
      return deckStartY + (exitDoor.y - deckStartY) * Math.pow(t, 1.7);
    });
    // The clearance floor's taper relaxes over the whole run-in, not just the
    // last few units: MEASURED, both batch-1 courses climb back toward their
    // exit, and a short taper made the floor overrule the descent into a
    // 70 u LEVEL PLATEAU — exactly the stranded-ball case this round
    // exists to kill. Relaxing it early keeps the fall unbroken.
    const tapers = pts.map((_, i) => clamp((exitDist - i * step) / CLEAR_RUNIN, 0, 1));
    const ys: number[] = pts.map((_, i) => {
      if (i * step >= exitDist) return grades[i]; // the in-road stub sits ON the road
      return Math.max(descents[i], grades[i] + cfg.deckClearance * tapers[i]);
    });
    for (let i = nExit - 1; i >= 0; i--) ys[i] = Math.max(ys[i], ys[i + 1]);
    for (let i = 0; i <= nExit; i++) ys[i] = Math.min(ys[i], deckStartY);
    // M117 batch 2 — pass 4, CREST RELIEF (Tokyo forced it, measured): when
    // the ground under the deck RISES above the catch height, the floor + the
    // catch-height cap above clip the profile into a dead-LEVEL shelf — on
    // Tokyo's expressway spur, 78 u of it right at the catch (the street
    // crests 8.9 at 19.5%, 0.16 u ABOVE the highest catchable deck start;
    // every catchable start on the city's ONE clean runway shelves, and no
    // spec knob escapes it: lift 8.5+ is uncatchable — measured sweep). A
    // level deck has ZERO chute pull, so a dead-stopped ball strands forever
    // — the exact case the owner's downhill rule exists to kill. GATED: only
    // a profile the passes above left with a level shelf is touched, so every
    // shelf-free channel (all of batch 1 + Napoli) is BYTE-IDENTICAL by
    // construction (test-pinned). The shelf is tilted into a minimum strict
    // fall, compressing the clearance toward shelfMinClearance over the
    // crest — the downhill rule outranks the sky-line floor. Bounds: never
    // below the pure descent line (keeps the exit anchored + the profile
    // monotone), never below the tapered minimum clearance (the deck may hug
    // a crest, it still never digs in). A ground spike taller than the whole
    // envelope would re-level a stretch — the never-uphill test is the guard.
    let hasShelf = false;
    for (let i = 1; i <= nExit && !hasShelf; i++) hasShelf = ys[i] === ys[i - 1];
    if (hasShelf) {
      const minFall = cfg.shelfGrade * step;
      for (let i = 1; i <= nExit; i++) {
        ys[i] = Math.max(
          Math.min(ys[i], ys[i - 1] - minFall),
          descents[i],
          grades[i] + cfg.shelfMinClearance * tapers[i],
        );
      }
    }

    const nodes: TrackNode[] = pts.map((p, i) => ({
      x: p.x,
      y: ys[i],
      z: p.z,
      dir: 0,
      rightX: 0,
      rightZ: 0,
    }));
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[Math.max(0, i - 1)];
      const b = nodes[Math.min(nodes.length - 1, i + 1)];
      nodes[i].dir = Math.atan2(b.x - a.x, b.z - a.z); // §6.1: atan2(dx, dz)
      nodes[i].rightX = rightX(nodes[i].dir);
      nodes[i].rightZ = rightZ(nodes[i].dir);
    }
    const surfacePts = pts; // aerial paths are all surface right-of-way

    const deckHalf = spec.style === 'cavern' ? cfg.cavernHalf : cfg.deckHalf;
    const channel = new Track(nodes, step, deckHalf, total);
    // Channels have no course end-walls: the exit hands off before the far
    // end, and the ENTRY must be open both ways — a ball-less lander backs
    // off the deck edge and drops home (owner rule), and a long-standoff
    // catch briefly flies BEFORE node 0, where a back wall would snap it.
    channel.noEndWalls = true;
    // …and they are CHUTES: the falling deck pulls a loose ball to the exit.
    channel.chuteRoll = cfg.chuteSlopeGravity;

    const leaveDist = total - Math.max(4, step * 1.5);
    const hand = channel.sampleAt(leaveDist);
    const exitHandArc = track.project(
      hand.x,
      hand.z,
      Math.round(exitArc / track.step),
    ).distance;

    // Visual identity only — physics never reads this. Default: a bare
    // 'highline' wears NYC's park dressing; cavern/skypath are one city each,
    // so their STYLE is their look and they stay undefined.
    const look: ShortcutLook | undefined =
      spec.look ?? (spec.style === 'highline' ? 'park' : undefined);

    const placed: PlacedShortcut = {
      style: spec.style,
      look,
      kind: 'jump',
      door,
      exitDoor,
      channel,
      entryArc,
      exitArc,
      leaveDist,
      doorDist: 0,
      exitHandArc,
      surfacePts,
      mouthY: deckStartY,
      sillY,
    };

    if (isSky) {
      placed.catch = {
        x: startX,
        z: startZ,
        dir: flightDir,
        y: deckStartY,
        sillY,
        half: cfg.catchHalf,
      };
    } else {
      placed.kicker = kicker;
    }
    return placed;
  });
}

/** What the crossing tests need to know about a body (pre-move kinematics). */
export interface CrossingBody {
  /** World position + planar velocity. */
  x: number;
  z: number;
  vx: number;
  vz: number;
  /** Vertical state (the sill extrapolations). */
  y: number;
  vy: number;
  /** Course-projected arc + lateral (from the MAIN track). */
  arc: number;
  lateral: number;
  /** Collision radius (car ≈ 1.1 half-width; ball = its radius). */
  radius: number;
  /** The body's own wall-clamp limit on the main track (wall entries only). */
  clampLimit: number;
}

/** Shared wall trigger: the body is about to hit its clamp line, moving
 *  outward. Returns the extrapolated (arc, y) at the WALL line, or null. */
function wallCrossing(
  door: DoorFrame,
  body: CrossingBody,
): { arcAtWall: number; yAtWall: number } | null {
  if (Math.sign(body.lateral) !== door.side) return null;
  const outward = (body.vx * door.rightX + body.vz * door.rightZ) * door.side;
  if (outward < tuning.shortcuts.minOutward) return null;
  const latAbs = Math.abs(body.lateral);
  // A ~3-step horizon: enough to pre-empt the lateral clamp (its approach is
  // gradual), TIGHT enough that the channel switch happens beside the deck's
  // start — a quarter-second horizon switched a fast flyer while it was
  // still BEHIND the channel's back end-wall, which snapped it forward ~14 u
  // in one step (measured). The extrapolated crossing stays exact.
  if (latAbs + outward * 0.05 < body.clampLimit) return null;
  const t = Math.max(0, door.wallLat - latAbs) / outward;
  const along = body.vx * door.dirX + body.vz * door.dirZ;
  return {
    arcAtWall: body.arc + along * t,
    yAtWall: body.y + body.vy * t - 0.5 * tuning.car.gravity * t * t,
  };
}

/**
 * WALL-APERTURE catch (cavern mouth / highline edge): the flying body must
 * pierce the wall inside the aperture's span AT OR ABOVE the sill — which
 * only a committed run over the kicker delivers. Too slow: the wall clamp
 * stands and you thud back down.
 */
export function mouthCross(sc: PlacedShortcut, body: CrossingBody, dt: number): boolean {
  void dt;
  if (sc.catch || sc.sillY === undefined) return false;
  const hit = wallCrossing(sc.door, body);
  if (!hit) return false;
  if (hit.yAtWall < sc.sillY) return false;
  return Math.abs(hit.arcAtWall - sc.door.arc) <= sc.door.half - body.radius;
}

/**
 * OVERHEAD catch (skypath): a landing PORCH, not a knife-edge — the deck's
 * front stretch catches any body arriving over it at/above the sill. A
 * porch, because player launch lines spread ±10° around the lip tangent
 * (the road curves under a straight flight — measured: a plane-only catch
 * missed clean committed jumps). Road traffic passes UNDER it untouched —
 * the height test is the whole gate.
 */
export function deckCatch(sc: PlacedShortcut, body: CrossingBody, dt: number): boolean {
  void dt;
  const c = sc.catch;
  if (!c) return false;
  const fx = forwardX(c.dir);
  const fz = forwardZ(c.dir);
  const along = (body.x - c.x) * fx + (body.z - c.z) * fz; // 0 = the porch's leading edge
  const alongVel = body.vx * fx + body.vz * fz;
  if (alongVel < tuning.shortcuts.minOutward) return false;
  if (along > tuning.shortcuts.catchDepth) return false; // sailed past the porch
  const t = Math.max(0, -along) / alongVel;
  // Tight horizon (see wallCrossing): catch only when genuinely arriving —
  // an early switch would put the body behind the channel's back end-wall.
  if (t > 0.05) return false;
  const yAt = body.y + body.vy * t - 0.5 * tuning.car.gravity * t * t;
  if (yAt < c.sillY) return false;
  const rx = rightX(c.dir);
  const rz = rightZ(c.dir);
  const acrossAt = (body.x + body.vx * t - c.x) * rx + (body.z + body.vz * t - c.z) * rz;
  return Math.abs(acrossAt) <= c.half - body.radius;
}

/** Kicker surface height above local ground at a world position (0 = off it). */
export function kickerHeight(k: KickerFrame, x: number, z: number): number {
  const fx = forwardX(k.dir);
  const fz = forwardZ(k.dir);
  const dx = x - k.x;
  const dz = z - k.z;
  const along = dx * fx + dz * fz;
  const across = dx * rightX(k.dir) + dz * rightZ(k.dir);
  if (Math.abs(across) > k.halfWidth) return 0;
  const t = along / k.length;
  if (t <= 0 || t >= 1) return 0;
  return t * k.height;
}

/** Forward speed ALONG the kicker's launch axis (only that converts to lift). */
export function kickerForwardSpeed(k: KickerFrame, vx: number, vz: number): number {
  return Math.max(0, vx * forwardX(k.dir) + vz * forwardZ(k.dir));
}

/**
 * ECONOMY MODEL (test + placement sanity — never used at runtime): estimate
 * road time between two arcs vs. the deck ride. The road model prices what
 * curves REALLY cost in this game — ball control (punts bank off walls, the
 * cradle sheds on hard cuts): measured human pace drops from ~46 flat-out
 * toward ~29 through hard bends (the owner's own traces, M77).
 */
export function estimateRoadTime(track: Track, fromArc: number, toArc: number): number {
  const curv = nodeCurvatures(track);
  const n0 = Math.max(0, Math.round(fromArc / track.step));
  const n1 = Math.min(curv.length, Math.round(toArc / track.step));
  let time = 0;
  for (let i = n0; i < n1; i++) {
    const degPerU = (curv[i] * 180) / Math.PI;
    const v = 46 * (1 - Math.min(0.38, degPerU * 0.3));
    time += track.step / v;
  }
  return time;
}

/** Deck traversal estimate: an open elevated line is clean, straight driving
 *  (rails keep the ball on your nose); the flight + lining up costs a beat. */
export function estimateChannelTime(sc: PlacedShortcut): number {
  return sc.channel.length / 44 + 0.55;
}

/** A span of guardrail wall the TrackMesh must NOT draw: the cavern's cliff
 *  face replaces its stretch, and every EXIT opening is a real gap. Physics
 *  is untouched by these — the lateral clamp stands everywhere except the
 *  placed catch logic, so a decoy surface bumps exactly like the wall. */
export interface WallGap {
  side: -1 | 1;
  fromArc: number;
  toArc: number;
}

/** The world cuts a city's shortcuts demand of the TrackMesh. */
export function visualCutouts(
  track: Track,
  placed: readonly PlacedShortcut[],
): { wallGaps: WallGap[] } {
  void track;
  const wallGaps: WallGap[] = [];
  for (const sc of placed) {
    if (sc.style === 'cavern') {
      // The rock face replaces the wall under the cavern mouth.
      wallGaps.push({ side: sc.door.side, fromArc: sc.door.arc - 20, toArc: sc.door.arc + 16 });
    }
    wallGaps.push({
      side: sc.door.side,
      fromArc: sc.exitDoor.arc - sc.exitDoor.half - 1.2,
      toArc: sc.exitDoor.arc + sc.exitDoor.half + 1.2,
    });
  }
  return { wallGaps };
}
